/**
 * @module
 *
 * @import {UserData} from "./model/user-data.js"
 */
import angular from 'angular';
import {max} from '@acng/frontend-bounty/std/number.js';
import {assign} from '@acng/frontend-bounty/object.js';
import {guard, OWNED} from '@acng/frontend-rubicon';

import {authUser, freeCoinsConfig, hasFeature, make} from '../core/service/env.js';
import {portal} from '../core/portal.js';
import {inject} from '../core/service/ng.js';
import {guestData} from './model/guest-data.js';
import {AgeVerification} from './age-verification.js';

/**
 * @implements {UserData}
 */
class User {
  preview = false;

  /**
   * @param {UserData} data
   */
  constructor(data) {
    ASSERT: {
      this.id = data.id;
      this.pool_id = data.pool_id;
      this.partner_pool = data.partner_pool;
      this.uid = data.uid;
      this.email = data.email;
      this.coins = data.coins;
      this.points = data.points;
      this.level = data.level;
      this.split_tests = data.split_tests;
      this.preferred_language = data.preferred_language;
      this.signup_wl_id = data.signup_wl_id;
      this.signup_type = data.signup_type;
      this.first_login = data.first_login;
      this.confirmed_at = data.confirmed_at;
      this.tester = data.tester;
      this.first_login_country = data.first_login_country;
      this.use_utm = data.use_utm;
      this.mailing = data.mailing;
      this.active_features = data.active_features;
      this.languages = data.languages;
      this.usergroups = data.usergroups;
      this.redirectPath = data.redirectPath;
      this.nickname = data.nickname;
      this.guest = data.guest;
      this.fsk = data.fsk;
      this.niche = data.niche;
      this.list_sorting_suffix = data.list_sorting_suffix;
      this.is_international = data.is_international;
      this.doi_at = data.doi_at;
      this.doi_reward_group = data.doi_reward_group;
      this.password_reset_token = data.password_reset_token;
      this.avs = data.avs;

      guard(this, OWNED(data), 'Initial user data');
    }

    assign(this, data);
  }

  get name() {
    return this.nickname;
  }

  /**
   * @type {"de" | "en"}
   */
  get lang() {
    return this.preferred_language;
  }

  get underage() {
    return this.fsk < 18;
  }

  get international() {
    return this.is_international;
  }

  get domestic() {
    return !this.international;
  }

  get adult() {
    return !this.underage;
  }

  get free() {
    return this.level < 10;
  }

  get premium() {
    return this.level >= 10;
  }

  get vip() {
    return this.level >= 20;
  }

  get noVip() {
    return !this.vip;
  }

  get doiReward() {
    /* Test it on doi-hint and payment overlay */
    // return {coins: 25, points: null}

    if (this.doi_at) {
      return null;
    }

    const freeCoinsForDoi = this.doi_reward_group == 'free_coins';

    return {
      coins: freeCoinsForDoi ? freeCoinsConfig.value : null,
      points: freeCoinsForDoi ? null : 100,
    };
  }

  hasGames() {
    return !this.guest && hasFeature('games');
  }

  hasVoiceMessage() {
    return !this.guest && hasFeature('messenger');
  }

  /**
   * @param {string} [reason]
   * @param {Record<string, string>} [params]
   */
  guestSignup(reason, params) {
    if (this.guest) {
      const onsOverlay = inject('onsOverlay');

      if (this.preview) {
        return true;
      }

      if (onsOverlay.count()) {
        DEBUG: console.warn('Overlay already open', onsOverlay.count());

        return true;
      }

      onsOverlay
        .create('signup', {reason: reason, reasonParams: angular.toJson(params)})
        .disableBackdrop()
        .open();

      return true;
    }

    return false;
  }

  /**
   * @param {number} fsk
   * @param {string} [reason]
   * @param {Record<string, string>} [params]
   */
  fulfillsFskRequirement(fsk, reason, params) {
    if (this.fsk >= fsk) {
      return true;
    }

    if (this.avs?.service) {
      make(AgeVerification).popup();
    } else {
      inject('payment').overlay(reason, params);
    }

    return false;
  }

  /**
   * @param {string} path
   */
  goto(path) {
    if (!this.guestSignup()) {
      inject('$location').url(path);
    }
  }

  /**
   * @deprecated
   *
   * @todo Move implementation to user/auth/logout.js
   */
  clear() {
    if (this.guest) {
      const ERR_LOGOUT_GUEST = 'A guest cannot logout.';

      throw Error(ERR_LOGOUT_GUEST);
    }

    history.pushState(null, '', '#/?logout');
    location.reload();
  }

  getFirstLogin() {
    return new Date(this.first_login);
  }

  /**
   * @param {number} days
   */
  checkFirstLoginWithinDays(days) {
    return this.getFirstLogin().getTime() >= Date.now() - days * 24 * 60 * 60 * 1000;
  }

  getDays() {
    const days = Math.ceil((Date.now() - this.getFirstLogin().getTime()) / (24 * 60 * 60 * 1000));
    return days;
  }

  /**
   * @param {number} splitTestId
   * @param {number} splitTestGroup
   */
  isInSplittyGroup(splitTestId, splitTestGroup) {
    return (
      (this.split_tests || []).find(
        (element) => element.id == splitTestId && element.group == splitTestGroup,
      ) !== void 0
    );
  }

  /**
   * @param {string} groupName
   */
  isInGroup(groupName) {
    if (!Array.isArray(this.usergroups)) {
      return false;
    }
    return this.usergroups.find((v) => v.name == groupName);
  }

  usesMetricUnits() {
    return !this.isInGroup('uk');
  }

  /**
   * @param {string[]} niche_tags
   */
  hasNiche(niche_tags) {
    return Array.isArray(niche_tags) && niche_tags.includes(this.niche);
  }

  /**
   * @returns {18 | 16 | 12}
   */
  getListFsk() {
    // @ts-ignore
    return max(portal.list_base_fsk, this.fsk);
  }

  ageVerificationPopupAvailable() {
    return this.underage && (this.avs?.service || this.avs?.disclaimer);
  }
}

const user = new User(authUser ?? guestData);

export {user};
